/* global document, Office */

import { ApiError, postMessage } from "../shared/api";
import { CONFIG } from "../shared/config";
import { readCurrentMessage } from "../shared/message";

/**
 * The AroundLink pane. Outlook has no "a message was read" event, but a PINNED pane receives
 * `ItemChanged` each time the user opens another message. That is what sends each opened
 * message automatically — provided the pane stays pinned.
 *
 * A message is sent only when the configured keyword appears in its subject or body (empty
 * keyword = every message). The manual button ignores the keyword and sends on demand.
 */

/** Messages already sent this session, so re-opening one does not send it twice. */
const sent = new Set();

const el = (id) => document.getElementById(id);

function setStatus(kind, text) {
  const status = el("status");
  status.className = `al-status al-status--${kind}`;
  status.textContent = text;
}

function matchesKeyword(payload) {
  const keyword = (CONFIG.keyword || "").trim().toLowerCase();
  if (keyword === "") {
    return true;
  }
  return (
    (payload.subject || "").toLowerCase().includes(keyword) ||
    (payload.body || "").toLowerCase().includes(keyword)
  );
}

/**
 * @param {boolean} manual true when the user clicked the button: then the keyword filter and the
 *                         "already sent" guard are bypassed.
 */
async function handleMessage(manual) {
  const item = Office.context.mailbox.item;
  if (!item) {
    el("subject").textContent = "";
    setStatus("idle", "Open a message.");
    return;
  }

  el("subject").textContent = item.subject || "(no subject)";
  const key = item.internetMessageId || item.itemId;

  if (!manual && key && sent.has(key)) {
    setStatus("ok", "Already sent to AroundLink.");
    return;
  }

  setStatus("busy", "Reading the message…");

  let payload;
  try {
    payload = await readCurrentMessage();
  } catch (error) {
    setStatus("error", `Could not read the message: ${error.message}`);
    return;
  }

  // Automatic path: skip messages that do not match the keyword — nothing is sent for them.
  if (!manual && !matchesKeyword(payload)) {
    setStatus("idle", "This message does not match the keyword — not sent.");
    return;
  }

  el("send").disabled = true;
  setStatus("busy", "Sending to AroundLink…");
  try {
    await postMessage(CONFIG, payload);
    if (key) {
      sent.add(key);
    }
    // The user may have moved to another message while this one was being sent.
    if (Office.context.mailbox.item === item) {
      setStatus("ok", "Sent to AroundLink.");
    }
  } catch (error) {
    if (Office.context.mailbox.item === item) {
      setStatus(
        "error",
        error instanceof ApiError ? error.message : `Unexpected error: ${error.message}`
      );
    }
  } finally {
    el("send").disabled = false;
  }
}

Office.onReady((info) => {
  if (info.host !== Office.HostType.Outlook) {
    setStatus("error", "This add-in only works in Outlook.");
    return;
  }

  el("send").addEventListener("click", () => handleMessage(true));
  // Raised when the user opens another message while the pane is pinned.
  Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => handleMessage(false));

  handleMessage(false);
});
